// Modules
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { uniqBy } from "lodash";

// Redux
import {
	getUserList,
	getUserGroupList,
	isUserSearchKeyPressed,
	groupsListSelector,
	selectedFilterData,
	getLoadingStateForUserList,
	selectedRecordCount,
	selectedCurrentGroup
} from "redux/userList/userSlice";

// Hooks
import { useAppDispatch, useAppSelector } from "app/hooks/hooks";

// Utils
import { get2FAStatus, getRowsPerPage } from "utils/Generic/generic";

// styles
import styles from "containers/RoleManagement/roleManagementComponent.module.css";

// Constant
import { Filters as FilterConstant, FilterStatusSort } from "constants/filters";
import { RoleManagement as Constants, Routes, DateTimeFormats } from "constants/global";

// Type
import { UserDetail } from "containers/RoleManagement/useRoleManagement";
import { GroupDetailsType } from "components/atoms/ExpandedRowTable/ExpandedRowTable";

// Component
import ViewEdit from "components/atoms/ViewEdit/ViewEdit";

export type ColumnHeaderRowType = {
	title: string;
	dataIndex: string;
	align?: "left" | "right" | "center";
};

export type TableDataType = {
	[key: string]: string;
};

export type ExpandedRowTableProps = {
	handlePagination?: (pageNumber: number, groupCode?: string) => void;
	columnHeaderData?: Array<ColumnHeaderRowType>; // Type for column headers
	tableData?: Array<TableDataType>; // Use || to add additional Table Data types
	groupDetails?: GroupDetailsType;
	searchUser?: string;
	searchTenantCode?: string;
};

export type UserGroupType = {
	[key: string]: Array<UserDetail>;
};

type UserDataType = {
	key: number;
	created_on: string;
	groups: string;
	mfa_enabled?: string;
	username: string;
	first_name: string;
	last_name: string;
	email: string;
	active?: JSX.Element;
	activeValue: string;
	viewOrEdit: JSX.Element;
	groupCodeList: Array<string>;
	createdDate: string;
};
export type PageSizeObjectType = {
	[key: string]: number;
};
export type UserListPayloadType = { page_number?: number; page_records?: number; user_search_text?: string; group_code?: string; tenant_code?: string };

// This method will show the status of user in formated way
export const getUserStatus = (status: string) => {
	switch (status) {
		case "X":
			return <span className={styles?.activeStatus}>Active</span>;
		case "":
			return <span className={styles?.deactiveStatus}>Deactivated</span>;
	}
};

export const useExpandedRowTable = ({ groupDetails = { group_code: "1", group_desc: "", group_id: "", active: "" }, searchUser }: ExpandedRowTableProps) => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const userGroup: UserGroupType = {};
	const userList = useAppSelector(getUserGroupList);
	const selectedFilter = useAppSelector(selectedFilterData);
	const selectedGroup = useAppSelector(selectedCurrentGroup);
	const recordCount = useAppSelector(selectedRecordCount);
	const loading = useAppSelector(getLoadingStateForUserList) as unknown as number;
	const groupListData = useAppSelector(groupsListSelector);
	const enterKeyPressed = useAppSelector(isUserSearchKeyPressed);
	const { data } = groupListData || {};

	// This will provide the row per page for each group
	const pageSize = useMemo(() => {
		const pageSizeObj: PageSizeObjectType = {};
		pageSizeObj[selectedGroup] = getRowsPerPage(recordCount, selectedGroup);
		return pageSizeObj;
	}, [recordCount]);

	// This method will provide the total record count
	const totalRecordCount = useMemo(() => {
		if (userList && groupDetails && userList[groupDetails.group_code]) {
			return userList[groupDetails.group_code].record_count;
		}
	}, [userList]);

	// This method will provide the group description for each user
	const getUserGroups = (groupList: Array<string>) => {
		const groupNames: Array<string> = [];
		data &&
			data?.map((item) => {
				if (groupList.includes(item.group_code)) {
					groupNames.push(item.group_code);
				}
			});

		return groupNames.toString().length > 20 ? groupNames.toString().substring(0, 20) + "..." : groupNames.toString();
	};

	// On pagination getUserList will be called
	const handlePagination = (pageNumber: number, group?: GroupDetailsType) => {
		const payLoad: UserListPayloadType = {
			page_number: pageNumber,
			page_records: getRowsPerPage(recordCount, selectedGroup)
		};

		if (group && group.group_code !== Constants.ALL_USERS_GROUP.group_code) {
			payLoad.group_code = group.group_code;
		}

		if (searchUser) {
			payLoad["user_search_text"] = searchUser;
		}

		dispatch(getUserList(payLoad));
	};

	// Based on filter selection (number of rows per page ) getUserList will be called
	useEffect(() => {
		if (recordCount) {
			const payLoad: UserListPayloadType = {
				page_number: 1,
				page_records: getRowsPerPage(recordCount, selectedGroup)
			};

			if (searchUser && selectedGroup === Constants.ALL_USERS_GROUP.group_code) {
				payLoad["user_search_text"] = searchUser;
			}
			if (selectedGroup !== Constants.ALL_USERS_GROUP.group_code) {
				payLoad["group_code"] = groupDetails.group_code;
			}

			if (selectedGroup === groupDetails.group_code) {
				dispatch(getUserList(payLoad));
			}
		}
	}, [recordCount, selectedGroup, enterKeyPressed]);

	const getSortedList = (userData: Array<UserDataType>) => {
		const selectedFilterVal: string = selectedFilter[groupDetails.group_code] ?? "";
		if (selectedFilterVal === FilterConstant.ROLE_MANAGEMENT[0].values[2].value) {
			return userData.sort((obj1, obj2) => new Date(obj2.createdDate).setHours(0, 0, 0, 0) - new Date(obj1.createdDate).setHours(0, 0, 0, 0));
		}
		if (selectedFilterVal === FilterConstant.ROLE_MANAGEMENT[0].values[3].value) {
			return userData.sort((obj1, obj2) => new Date(obj1.createdDate).setHours(0, 0, 0, 0) - new Date(obj2.createdDate).setHours(0, 0, 0, 0));
		}
		return uniqBy(
			[
				...userData.filter((item) => {
					if (!selectedFilterVal) {
						return item.activeValue.toLowerCase() === "ACTIVE".toLowerCase();
					} else {
						return item.activeValue.toLowerCase() === FilterStatusSort[selectedFilterVal].toLowerCase();
					}
				}),
				...userData
			],
			"key"
		);
	};
	// Updating the userlist based on the group
	const updatedList = useMemo(() => {
		if (userList && userList[groupDetails.group_code]) {
			const userData: Array<UserDataType> = [];
			userGroup[groupDetails.group_code] = userList[groupDetails.group_code].user_list;
			userGroup[Constants.ALL_USERS_GROUP.group_code] = userList[Constants.ALL_USERS_GROUP.group_code]?.user_list;
			userGroup[groupDetails?.group_code]?.map((item: UserDetail) => {
				const userDetails = {
					key: item.user_id,
					created_on: moment(item?.created_on)?.format(DateTimeFormats.USE_DD_MMM),
					groups: getUserGroups(item.groups),
					mfa_enabled: get2FAStatus(item.mfa_enabled),
					username: item.username,
					first_name: item.first_name,
					last_name: item.last_name,
					email: item.email,
					active: getUserStatus(item.active),
					activeValue: item.active ? Constants.USER_GROUP_LIST.STATUS[0] : Constants.USER_GROUP_LIST.STATUS[1],
					viewOrEdit: <ViewEdit onClick={() => navigate(Routes.VIEW_EDIT_ADMIN_USER, { state: { username: item?.username } })} />,
					groupCodeList: item.groups,
					createdDate: item.created_on
				};
				userData.push(userDetails);
			});
			return getSortedList(userData);
		}
	}, [userList, selectedFilter]);

	return {
		handlePagination,
		updatedList,
		loading,
		pageSize,
		totalRecordCount
	};
};
